import { createContext, type PropsWithChildren, useContext } from "react";
import type { SvgrComponent } from "@b2bportal/core-types";
import { TenantIconNameEnum, TenantImageNameEnum } from "@tenant/types";
import {
  type HotelsAmenityIconSet,
  DisruptionIconNameEnum,
  CFARIconNameEnum,
  PriceFreezeIconNameEnum,
  CarIconName,
  InsuranceIconNameEnum,
} from "./product";

export interface IAssetContextProps {
  icons?: IconSet;
  images?: ImageSet;
}

export enum CoreImageNameEnum {
  flightBookingConfirmationImage = "flightBookingConfirmationImage",
  walletCardImage = "walletCardImage",
  logo = "logo",
  homepageBackground = "homepageBackground",
  pageBackground = "pageBackground",
  flightLoadingImage = "flightLoadingImage",
  hotelLoadingImage = "hotelLoadingImage",
  carLoadingImage = "carLoadingImage",
  tripsLoadingImage = "tripsLoadingImage",
  tripsHeadingBackground = "tripsHeadingBackground",
  roomPlaceholder = "roomPlaceholder",
  htsPoweredByLogo = "htsPoweredByLogo",
}

export type CoreImageSet = {
  [key in CoreIconNameEnum]: IconType;
};

export type IconType = SvgrComponent;

export enum CoreIconNameEnum {
  ticket = "ticket",
  itinerary = "itinerary",
  trips = "trips",
  info = "info",
  cfar__notAllowed = "cfar__notAllowed",
  infoFilled = "infoFilled",
  warning = "warning",
  error = "error",
  dropdownTriangle = "dropdownTriangle",
  dropdownArrow = "dropdownArrow",
  backIcon = "backIcon",
  actionIcon = "actionIcon",
  chevronRight = "chevronRight",
  calendarSeparatorIcon = "calendarSeparatorIcon",
  fareDetailsAvailable = "fareDetailsAvailable",
  fareDetailsUnavailable = "fareDetailsUnavailable",
  fareDetailsCharge = "fareDetailsCharge",
  fareDetailsGeneric = "fareDetailsGeneric",
  filter = "filter",
  close = "close",
  nextIcon = "nextIcon",
  airplane = "airplane",
  airplaneDiagonal = "airplaneDiagonal",
  airplaneArrive = "airplaneArrive",
  airplaneDeparture = "airplaneDeparture",
  calendar = "calendar",
  filledCalendar = "filledCalendar",
  plus = "plus",
  plusCircle = "plusCircle",
  minus = "minus",
  mapPin = "mapPin",
  searchForFlights = "searchForFlights",
  passengerSelection = "passengerSelection",
  check = "check",
  checkFilled = "checkFilled",
  edit = "edit",
  chevronDown = "chevronDown",
  chevronUp = "chevronUp",
  guest = "guest",
  users = "users",
  search = "search",
  externalLink = "externalLink",
  carouselRightArrow = "carouselRightArrow",
  carouselLeftArrow = "carouselLeftArrow",
  tripadvisor = "tripadvisor",
  starFilled = "starFilled",
  bed = "bed",
  checkCircle = "checkCircle",
  warningCircle = "warningCircle",
  premiumDisruptionAssistance = "premiumDisruptionAssistance",
  dismiss = "dismiss",
  hotel = "hotel",
  flight = "flight",
  currency = "currency",
  rightArrow = "rightArrow",
  credits = "credits",
  map = "map",
  list = "list",
  baggageChecked = "baggageChecked",
  flightOutline = "flightOutline",
  warningTriangle = "warningTriangle",
  coins = "coins",
  tenantLogo = "tenantLogo",
  disruptionExerciseFullyCoveredIcon = "disruptionExerciseFullyCoveredIcon",
  disruptionNoFlightsIcon = "disruptionNoFlightsIcon",
  tripCardLink = "tripCardLink",
  disruptionOriginSegmentIcon = "disruptionOriginSegmentIcon",
  car = "car",
  clock = "clock",
  support__chat = "support__chat",
  support__chevronRight = "support__chevronRight",
  support__phone = "support__phone",
  support__phoneIntl = "support__phoneIntl",
  support__sendMessage = "support__sendMessage",
  support__starEmpty = "support__starEmpty",
  support__starFilled = "support__starFilled",
  world = "world",
  ui__accordionAction = "ui__accordionAction",
  base__genericError = "base__genericError",
  ui__mapMarker = "ui__mapMarker",
  ui__infoPopover = "ui__infoPopover",
  wishlist__heart = "wishlist__heart",
  offers__tag = "offers_tag",
  cfar__poweredBy = "cfar__poweredBy",
  cfar__info = "cfar__info",
  cfar__checkout = "cfar__checkout",
  cfar__shieldInfo = "cfar__shieldInfo",
  cvv = "cvv",
  confirmation = "confirmation",
  htsPoweredByLogo = "htsPoweredByLogo",
}

export enum CrossSellImageNameEnum {
  crossSellActivity = "crossSellActivity",
  crossSellCar = "crossSellCar",
  crossSellInsurance = "crossSellInsurance",
}

export type ImageNameEnum =
  | CoreImageNameEnum
  | CrossSellImageNameEnum
  | TenantImageNameEnum;
export const ImageNameEnum = {
  ...CoreImageNameEnum,
  ...CrossSellImageNameEnum,
  ...TenantImageNameEnum,
};

export type IconNameEnum =
  | CoreIconNameEnum
  | TenantIconNameEnum
  | DisruptionIconNameEnum
  | InsuranceIconNameEnum
  | CFARIconNameEnum
  | PriceFreezeIconNameEnum
  | CarIconName;

export const IconNameEnum = {
  ...CoreIconNameEnum,
  ...TenantIconNameEnum,
  ...DisruptionIconNameEnum,
  ...InsuranceIconNameEnum,
  ...CFARIconNameEnum,
  ...PriceFreezeIconNameEnum,
  ...CarIconName,
};

export type ImageSet = {
  [key in ImageNameEnum]?: string;
};
export type IconSet = {
  [key in IconNameEnum]?: IconType;
} & {
  hotelAmenities?: HotelsAmenityIconSet;
};

export interface IAssetContextProvider {
  context: IAssetContextProps;
}

const AssetContext = createContext<IAssetContextProps | undefined>(undefined);

export function useAssetContext(): IAssetContextProps {
  const ctx = useContext(AssetContext);
  if (ctx == null) {
    throw new Error(`must be used within a AssetContextProvider`);
  }
  return ctx;
}

export const useTenantIcons = (): IconSet => {
  const ctx = useAssetContext();
  return ctx.icons ?? {};
};

export const useTenantImages = (): ImageSet => {
  const ctx = useAssetContext();
  return ctx.images ?? {};
};

export const AssetContextProvider = ({
  children,
  context,
}: PropsWithChildren<IAssetContextProvider>) => {
  return (
    <AssetContext.Provider value={context}>{children}</AssetContext.Provider>
  );
};

export const useTenantHotelAmenitiesIcons = (): HotelsAmenityIconSet => {
  const ctx = useAssetContext();
  return ctx.icons?.hotelAmenities ?? {};
};
